import React, { useCallback, useEffect, useReducer, useState } from "react";
import {
  EMAIL_TRIGGER_DEFINITIONS,
  loadEmailTriggersSettings,
  saveEmailTriggersSettings,
} from "./emailTriggersStorage";
import EmailTemplateFieldInput from "./EmailTemplateFieldInput";
import { EMAIL_TRIGGER_ATTACHMENT_SOURCES } from "./emailTriggerAttachments";
import { loadUserMatrixSettings } from "./userMatrixStorage";

function parseEmailsFromText(text) {
  return [
    ...new Set(
      String(text || "")
        .split(/[\s,;]+/)
        .map((e) => e.trim().toLowerCase())
        .filter((e) => e && e.includes("@"))
    ),
  ].sort();
}

export default function EmailTriggersPluginPanel() {
  const [settings, setSettings] = useState(() => loadEmailTriggersSettings());
  const [, bumpMatrixRev] = useReducer((x) => x + 1, 0);
  const [modalTriggerId, setModalTriggerId] = useState(null);
  const [draftSubject, setDraftSubject] = useState("");
  const [draftBody, setDraftBody] = useState("");
  const [draftAttachmentSources, setDraftAttachmentSources] = useState([]);
  const [directEmailDrafts, setDirectEmailDrafts] = useState(() => {
    const out = {};
    const s = loadEmailTriggersSettings();
    for (const def of EMAIL_TRIGGER_DEFINITIONS) {
      if (!def.directRecipientEmails) continue;
      const emails = s.triggers?.[def.id]?.recipientEmails;
      out[def.id] = Array.isArray(emails) ? emails.join(", ") : "";
    }
    return out;
  });

  const matrix = loadUserMatrixSettings();
  const roleTypes = matrix.userTypes || [];

  const closeModal = useCallback(() => {
    setModalTriggerId(null);
  }, []);

  useEffect(() => {
    if (!modalTriggerId) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalTriggerId, closeModal]);

  useEffect(() => {
    const on = () => bumpMatrixRev();
    window.addEventListener("pb-user-matrix-settings-changed", on);
    return () => window.removeEventListener("pb-user-matrix-settings-changed", on);
  }, []);

  const persist = useCallback((next) => {
    setSettings(next);
    void saveEmailTriggersSettings(next);
  }, []);

  const toggleRecipientRole = useCallback(
    (triggerId, roleTypeId, checked) => {
      const t = settings.triggers[triggerId] || {
        recipientRoleTypeIds: [],
        recipientEmails: [],
        subject: "",
        body: "",
      };
      const set = new Set(t.recipientRoleTypeIds || []);
      if (checked) set.add(roleTypeId);
      else set.delete(roleTypeId);
      const recipientRoleTypeIds = [...set].sort();
      persist({
        ...settings,
        triggers: {
          ...settings.triggers,
          [triggerId]: { ...t, recipientRoleTypeIds },
        },
      });
    },
    [settings, persist]
  );

  const setRecipientEmailsText = useCallback(
    (triggerId, text) => {
      setDirectEmailDrafts((prev) => ({ ...prev, [triggerId]: text }));
    },
    []
  );

  const commitRecipientEmails = useCallback(
    (triggerId, text) => {
      const t = settings.triggers[triggerId] || {
        recipientRoleTypeIds: [],
        recipientEmails: [],
        subject: "",
        body: "",
      };
      const recipientEmails = parseEmailsFromText(text);
      setDirectEmailDrafts((prev) => ({ ...prev, [triggerId]: recipientEmails.join(", ") }));
      persist({
        ...settings,
        triggers: {
          ...settings.triggers,
          [triggerId]: { ...t, recipientEmails },
        },
      });
    },
    [settings, persist]
  );

  const openModal = useCallback(
    (triggerId) => {
      const t = settings.triggers[triggerId];
      setDraftSubject(t?.subject ?? "");
      setDraftBody(t?.body ?? "");
      setDraftAttachmentSources(Array.isArray(t?.attachmentSources) ? [...t.attachmentSources] : []);
      setModalTriggerId(triggerId);
    },
    [settings.triggers]
  );

  const saveModal = useCallback(() => {
    if (!modalTriggerId) return;
    const t = settings.triggers[modalTriggerId] || {
      recipientRoleTypeIds: [],
      recipientEmails: [],
      subject: "",
      body: "",
      attachmentSources: [],
    };
    persist({
      ...settings,
      triggers: {
        ...settings.triggers,
        [modalTriggerId]: {
          ...t,
          subject: draftSubject,
          body: draftBody,
          attachmentSources: [...draftAttachmentSources],
        },
      },
    });
    closeModal();
  }, [modalTriggerId, draftSubject, draftBody, draftAttachmentSources, settings, persist, closeModal]);

  const resolvedRecipientsByTrigger = (() => {
    const out = {};
    for (const def of EMAIL_TRIGGER_DEFINITIONS) {
      const t = settings.triggers[def.id] || { recipientRoleTypeIds: [], recipientEmails: [] };
      if (def.directRecipientEmails) {
        out[def.id] = Array.isArray(t.recipientEmails) ? [...t.recipientEmails].sort() : [];
        continue;
      }
      const roleIds = t.recipientRoleTypeIds || [];
      const emails = new Set();
      for (const u of matrix.users || []) {
        if (u.status !== "active") continue;
        if (roleIds.includes(u.userTypeId)) emails.add(String(u.email || "").toLowerCase());
      }
      out[def.id] = [...emails].sort();
    }
    return out;
  })();

  const modalDef = EMAIL_TRIGGER_DEFINITIONS.find((d) => d.id === modalTriggerId);

  return (
    <div className="email-triggers-panel">
      <h3 className="email-triggers-panel-title">Email Triggers settings</h3>
      <p className="email-triggers-panel-lead">
        Choose recipient role types for each trigger, then open a trigger to set subject and body. Recipients resolve
        automatically from active users currently assigned to those roles. Some digests use a fixed system template
        (subject and body are not editable). The commercial AG Grid export uses typed To addresses instead of roles.
      </p>

      <section className="email-triggers-section" aria-labelledby="email-triggers-list-heading">
        <h4 id="email-triggers-list-heading" className="email-triggers-subheading">
          Triggers
        </h4>
        <div className="email-triggers-table-wrap">
          <table className="email-triggers-table">
            <thead>
              <tr>
                <th scope="col">Trigger</th>
                <th scope="col">Notify</th>
              </tr>
            </thead>
            <tbody>
              {(() => {
                let lastGroup = null;
                return EMAIL_TRIGGER_DEFINITIONS.map((def) => {
                  const group = def.group || "Milestones";
                  const showGroupHeader = group !== lastGroup;
                  lastGroup = group;
                  const t = settings.triggers[def.id];
                  const selected = new Set(t?.recipientRoleTypeIds || []);
                  const resolved = resolvedRecipientsByTrigger[def.id] || [];
                  const emailsText =
                    directEmailDrafts[def.id] != null
                      ? directEmailDrafts[def.id]
                      : Array.isArray(t?.recipientEmails)
                        ? t.recipientEmails.join(", ")
                        : "";
                  return (
                    <React.Fragment key={def.id}>
                      {showGroupHeader ? (
                        <tr className="email-triggers-group-row">
                          <td colSpan={2}>
                            <strong>{group}</strong>
                          </td>
                        </tr>
                      ) : null}
                      <tr>
                        <td className="email-triggers-trigger-cell">
                          <button
                            type="button"
                            className="email-triggers-trigger-link"
                            onClick={() => openModal(def.id)}
                          >
                            {def.label}
                          </button>
                        </td>
                        <td className="email-triggers-recipients-cell">
                          {def.assignmentRecipients ? (
                            <p className="email-triggers-muted" style={{ margin: 0, maxWidth: "28rem" }}>
                              <strong>To:</strong> the Permitting Specialist assigned on the project. If nobody is
                              assigned, every active Permitting Specialist. <strong>Cc:</strong> the Permitting Manager.
                            </p>
                          ) : def.directRecipientEmails ? (
                            <>
                              <label className="email-triggers-check-label" style={{ display: "block" }}>
                                <span className="email-triggers-muted" style={{ display: "block", marginBottom: 6 }}>
                                  To (comma or newline separated)
                                </span>
                                <textarea
                                  value={emailsText}
                                  onChange={(e) => setRecipientEmailsText(def.id, e.target.value)}
                                  onBlur={(e) => commitRecipientEmails(def.id, e.target.value)}
                                  rows={3}
                                  placeholder="ops@example.com, reports@example.com"
                                  style={{
                                    width: "100%",
                                    maxWidth: "28rem",
                                    boxSizing: "border-box",
                                    font: "inherit",
                                    padding: "8px 10px",
                                    border: "1px solid #cbd5e1",
                                    borderRadius: 8,
                                  }}
                                />
                              </label>
                              <div className="email-triggers-muted" style={{ marginTop: "0.35rem" }}>
                                To:{" "}
                                {resolved.length > 0 ? (
                                  resolved.map((email) => (
                                    <code key={email} style={{ marginRight: "0.4rem" }}>
                                      {email}
                                    </code>
                                  ))
                                ) : (
                                  "none"
                                )}
                              </div>
                            </>
                          ) : roleTypes.length === 0 ? (
                            <span className="email-triggers-muted">—</span>
                          ) : (
                            <>
                              <ul className="email-triggers-recipient-checks">
                                {roleTypes.map((role) => (
                                  <li key={role.id}>
                                    <label className="email-triggers-check-label">
                                      <input
                                        type="checkbox"
                                        checked={selected.has(role.id)}
                                        onChange={(e) =>
                                          toggleRecipientRole(def.id, role.id, e.target.checked)
                                        }
                                      />
                                      <span>{role.name}</span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                              <div className="email-triggers-muted" style={{ marginTop: "0.35rem" }}>
                                Resolved recipients:{" "}
                                {resolved.length > 0 ? (
                                  resolved.map((email) => (
                                    <code key={email} style={{ marginRight: "0.4rem" }}>
                                      {email}
                                    </code>
                                  ))
                                ) : (
                                  "none"
                                )}
                              </div>
                            </>
                          )}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </section>

      {modalTriggerId && modalDef ? (
        <div
          className="email-triggers-modal-backdrop"
          role="presentation"
          onClick={closeModal}
        >
          <div
            className="email-triggers-modal"
            style={{ maxWidth: "40rem" }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="email-triggers-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="email-triggers-modal-title" className="email-triggers-modal-title">
              Email: {modalDef.label}
            </h3>
            {modalDef.hardcodedTemplate ? (
              <>
                <p className="email-triggers-modal-sub">
                  {modalDef.directRecipientEmails
                    ? "This trigger sends a fixed subject/body with a JSON attachment. Set To addresses on the triggers list."
                    : "This trigger sends a system-generated progress summary. Subject and body are fixed; only recipient roles (on the triggers list) control who receives it."}
                </p>
                <div
                  style={{
                    margin: "12px 0 0",
                    padding: "12px 14px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    background: "#f8fafc",
                    fontSize: 13,
                    lineHeight: 1.45,
                    color: "#334155",
                  }}
                >
                  {modalDef.id === "commercial_ag_grid_weekly_export" ? (
                    <>
                      <p style={{ margin: "0 0 8px" }}>
                        <strong>When:</strong> 8:00 AM every Monday (America/Los_Angeles).
                      </p>
                      <p style={{ margin: "0 0 8px" }}>
                        <strong>Content:</strong> JSON of all commercial projects using administrator-visible AG Grid
                        columns (slug keys).
                      </p>
                      <p style={{ margin: "0 0 8px" }}>
                        <strong>Subject:</strong> Weekly Commercial Projects Export
                      </p>
                      <p style={{ margin: 0 }}>
                        <strong>Recipients:</strong> Typed To addresses on the triggers list (not role types).
                      </p>
                    </>
                  ) : modalDef.id === "inspector_daily_summary" ? (
                    <>
                      <p style={{ margin: "0 0 8px" }}>
                        <strong>When:</strong> 8:00 AM Tue–Sat (America/Los_Angeles). Sunday and Monday mornings are
                        skipped.
                      </p>
                      <p style={{ margin: "0 0 8px" }}>
                        <strong>Content:</strong> Prior calendar day’s inspector reports — totals plus per-project
                        today’s progress, overall conduit/fiber %, projected completion work day (from that day’s
                        pace), contractor, and notes.
                      </p>
                      <p style={{ margin: 0 }}>
                        <strong>Subject:</strong> Daily Inspector Progress Report — [date]
                      </p>
                    </>
                  ) : (
                    <>
                      <p style={{ margin: "0 0 8px" }}>
                        <strong>When:</strong> 8:00 AM every Saturday (America/Los_Angeles).
                      </p>
                      <p style={{ margin: "0 0 8px" }}>
                        <strong>Content:</strong> Monday–Friday inspector reports for the week just ended — totals plus
                        per-project progress, overall %, and projected completion when fiber/conduit is reported.
                      </p>
                      <p style={{ margin: 0 }}>
                        <strong>Subject:</strong> Weekly Inspector Progress Report — [Mon] to [Fri]
                      </p>
                    </>
                  )}
                </div>
                <div className="email-triggers-modal-actions">
                  <button type="button" className="email-triggers-button email-triggers-button-ghost" onClick={closeModal}>
                    Close
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className="email-triggers-modal-sub">
                  Use <strong>Insert field</strong> next to Subject or Body to place a dynamic value at the cursor. Fields
                  come from the project record (work order, location, phase, flags, etc.). Booleans render as Yes/No when
                  the email is sent.
                  {modalDef.assignmentRecipients ? (
                    <>
                      {" "}
                      Recipients are set from the project&apos;s Permitting Specialist assignment. The Permitting Manager
                      is always copied.
                    </>
                  ) : null}
                </p>
                <EmailTemplateFieldInput
                  label="Subject"
                  value={draftSubject}
                  onChange={setDraftSubject}
                  placeholder="e.g. Permitting complete — "
                />
                <EmailTemplateFieldInput
                  label="Body"
                  value={draftBody}
                  onChange={setDraftBody}
                  multiline
                  rows={10}
                  placeholder="Message body…"
                />
                <fieldset
                  className="email-triggers-attachments-fieldset"
                  style={{
                    margin: "16px 0 0",
                    padding: "12px 14px",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    background: "#f8fafc",
                  }}
                >
                  <legend style={{ padding: "0 6px", fontSize: 13, fontWeight: 600 }}>File attachments</legend>
                  <p className="email-triggers-muted" style={{ margin: "0 0 10px", fontSize: 13 }}>
                    When this trigger fires, matching project files are attached to the outgoing email (requires SMTP).
                  </p>
                  <ul className="email-triggers-attachment-checks">
                    {EMAIL_TRIGGER_ATTACHMENT_SOURCES.map((src) => {
                      const selected = new Set(draftAttachmentSources);
                      return (
                        <li key={src.id}>
                          <label className="email-triggers-check-label">
                            <input
                              type="checkbox"
                              checked={selected.has(src.id)}
                              onChange={(e) => {
                                const next = new Set(draftAttachmentSources);
                                if (e.target.checked) next.add(src.id);
                                else next.delete(src.id);
                                setDraftAttachmentSources([...next]);
                              }}
                            />
                            <span>
                              <strong>{src.label}</strong>
                              <span className="email-triggers-muted"> — {src.description}</span>
                            </span>
                          </label>
                        </li>
                      );
                    })}
                  </ul>
                </fieldset>
                <div className="email-triggers-modal-actions">
                  <button type="button" className="email-triggers-button email-triggers-button-primary" onClick={saveModal}>
                    Save
                  </button>
                  <button type="button" className="email-triggers-button email-triggers-button-ghost" onClick={closeModal}>
                    Cancel
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
